<script lang="ts">
  import type { Other, HistoricalDate } from '../lib/types'
  import { Dialog, Input, Textarea, Label, Button, Checkbox } from '$lib/components/ui'

  interface Props {
    other: Other
    videoId: string
    onSave: (other: Other) => void
    onCancel: () => void
  }

  let { other, videoId, onSave, onCancel }: Props = $props()

  let name = $state(other.name)
  let description = $state(other.description || '')
  let aliases = $state(other.aliases?.join(', ') || '')
  let foundingYear = $state(other.founding?.year?.toString() || '')
  let foundingMonth = $state(other.founding?.month?.toString() || '')
  let foundingDay = $state(other.founding?.day?.toString() || '')
  let internalOnly = $state(other.internalOnly || false)
  let isSaving = $state(false)
  let open = $state(true)

  async function handleSave() {
    if (!name.trim()) return

    isSaving = true
    try {
      const aliasesList = aliases
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0)

      const founding: HistoricalDate | undefined = foundingYear
        ? {
            year: parseInt(foundingYear, 10),
            month: foundingMonth ? parseInt(foundingMonth, 10) : undefined,
            day: foundingDay ? parseInt(foundingDay, 10) : undefined,
          }
        : undefined

      const payload = {
        name: name.trim(),
        description: description.trim(),
        aliases: aliasesList,
        founding,
        internal_only: internalOnly,
        video_id: videoId,
      }

      const response = await fetch(`/api/others/${other.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const savedOther = await response.json()
        onSave(savedOther)
      }
    } finally {
      isSaving = false
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) onCancel()
  }
</script>

<Dialog bind:open onOpenChange={handleOpenChange} title="Edit other">
  <div class="space-y-4">
    <div>
      <Label for="name">Name</Label>
      <Input id="name" bind:value={name} placeholder="Name" />
    </div>

    <div>
      <Label for="aliases">Aliases (comma-separated)</Label>
      <Input id="aliases" bind:value={aliases} placeholder="Alternative names" />
    </div>

    <div>
      <Label for="description">Description</Label>
      <Textarea id="description" bind:value={description} placeholder="Description" rows={3} />
    </div>

    <div>
      <Label>Founding date</Label>
      <div class="flex gap-2">
        <Input
          type="number"
          bind:value={foundingYear}
          placeholder="Year"
          class="w-20"
        />
        <Input
          type="number"
          bind:value={foundingMonth}
          placeholder="Mo"
          class="w-14"
        />
        <Input
          type="number"
          bind:value={foundingDay}
          placeholder="Day"
          class="w-14"
        />
      </div>
    </div>

    <div class="flex items-center gap-2">
      <Checkbox bind:checked={internalOnly} />
      <span class="text-sm text-text-secondary">Internal only (don't match cross-video)</span>
    </div>

    <div class="flex justify-end gap-2 pt-4 border-t border-border-subtle">
      <Button variant="ghost" onclick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onclick={handleSave} disabled={isSaving || !name.trim()}>
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
</Dialog>
